import './Navbar.scss';

import { CaretLeft, Globe, WhatsappLogo } from '@phosphor-icons/react';
import { Button, getCurrencyCode, Icon, useConfig } from '@travelwin/core';
import classNames from 'classnames';
import { ForwardedRef, forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrency } from '../../context/Currency';
import { useNavbarItems } from '../../hooks';
import { useExternalHomepageConfig } from '../../hooks/useExternalHomepageConfig';
import { useLocaleUtils } from '../../hooks/useLocaleUtils';
import { NavItem } from '../../interfaces/NavBar';
import { FreeTrialBanner } from '../../pages/FreeTrialBanner/FreeTrialBanner';
import { getBrandSpecificImage, shopBrands } from '../../utils';
import { isRemix } from '../../utils/ssrUtils';
import { getPathnameWithLocale } from '../../utils/urlUtils';
import { Link, useNavigate } from '../Link';
import { ShowTo } from '../ShowTo';
import BurgerMenu from './BurgerMenu';
import {
  CurrencySwitch,
  FullScreenGlobalPreferencePanel,
  GlobalPreferencePanelButton,
  GlobalPreferencePanelDropDown,
  LanguageSwitch,
} from './GlobalPreferencePanel';
import CountrySwitch from './GlobalPreferencePanel/CountrySwitch';
import {
  globalPreferenceClassName,
  SelectorType,
} from './GlobalPreferencePanel/GlobalPreferencePanel';
import MenuItem from './MenuItem';

type Props = unknown;

const className = 'c-EcommerceNavbar';

const Navbar = (_props: Props, ref: ForwardedRef<HTMLElement>) => {
  const [expandedSubMenuId, setExpandedSubMenuId] = useState('');
  const [isBurgerMenuExpanded, setIsBurgerMenuExpanded] = useState(false);
  const [isActiveScroll, setIsActiveScroll] = useState(false);
  const [isGlobalPreferencePanelExpanded, setIsGlobalPreferencePanelExpanded] =
    useState(false);
  const [
    isFullScreenGlobalPreferencePanelOpened,
    setIsFullScreenGlobalPreferencePanelOpened,
  ] = useState(false);

  const navigate = useNavigate();
  const { WHITELABEL_BRAND, BRAND_NAME, WHATSAPP_CONTACT_NUMBER } = useConfig();
  const { t } = useTranslation();
  const {
    currentLocale: currentLanguage,
    supportedLocales: supportedLanguages,
  } = useLocaleUtils();
  const { navbarItems, contactBarItems } = useNavbarItems();
  const { shouldRedirectToExternalHomepage, getHomepageUrl } =
    useExternalHomepageConfig();
  const { currency } = useCurrency();
  const handleNavItemClick = (item: NavItem) => {
    const { to, items } = item;

    const hasSubMenu = Array.isArray(items) && items.length > 0;

    if (to && !hasSubMenu) {
      handleSubMenuClose();
      navItemOnClick(item);
    }

    if (hasSubMenu) {
      const id = item.id === expandedSubMenuId ? '' : item.id;
      setExpandedSubMenuId(id);
    }
  };

  const navItemOnClick = (item: NavItem) => {
    const { to, ecommerceRoute, items } = item;
    if (!to) return;

    const redirect = (link: string, ecommerceRoute: boolean) => {
      const hasSubMenu = Array.isArray(items) && items.length > 0;
      if (!hasSubMenu) {
        if (ecommerceRoute) {
          navigate(link);
        } else {
          window.location.href = link;
        }
      }
    };

    redirect(to, !!ecommerceRoute);
  };

  const handleSubMenuClose = () => {
    setIsBurgerMenuExpanded(false);
    setExpandedSubMenuId('');
  };

  const renderMenu = useMemo(() => {
    return navbarItems.map((item: NavItem) => {
      if (!item.visible) return null;
      return (
        <MenuItem
          key={item.id}
          item={item}
          variant="nav-bar"
          handleNavItemClick={handleNavItemClick}
          handleSubMenuClose={handleSubMenuClose}
        />
      );
    });
  }, [navbarItems]);

  const renderBurgerMenuButton = useMemo(
    () => (
      <button
        type="button"
        className={classNames(`${className}__burger-button`, {
          [`${className}__burger-button--expanded`]: isBurgerMenuExpanded,
        })}
        onClick={() => {
          if (isBurgerMenuExpanded) {
            handleSubMenuClose();
          } else {
            setIsBurgerMenuExpanded(true);
            setIsFullScreenGlobalPreferencePanelOpened(false);
          }
        }}
      >
        {isBurgerMenuExpanded ? (
          <Icon name="burgerMenuExpanded" />
        ) : (
          <Icon name="burgerMenu" />
        )}
      </button>
    ),
    [isBurgerMenuExpanded],
  );

  const brandImage = useMemo(
    () => (
      <img
        className={`${className}__brand-image`}
        src={getBrandSpecificImage('logo', WHITELABEL_BRAND)}
        alt={t('logo.altText', '{{brandName}} Logo', { brandName: BRAND_NAME })}
        width={128}
        height={64}
        data-testid="link-home"
      />
    ),
    [],
  );

  const handleStoreLocatorClicked = () => {
    if (isRemix()) {
      window.location.href = getPathnameWithLocale(
        '/store-locations',
        currentLanguage,
      );
    } else {
      window.location.href = getHomepageUrl('/store-locations');
    }
  };

  const togglePanelVisibility = (active: boolean) => {
    setIsGlobalPreferencePanelExpanded(active);
    setIsFullScreenGlobalPreferencePanelOpened(active);
  };

  const selectors: SelectorType[] = useMemo(
    () => [
      {
        id: 'language',
        title: t('selector.language', 'Language'),
        component: <LanguageSwitch className={globalPreferenceClassName} />,
        showTo: [...shopBrands],
        buttonLabel: (
          <span className={`${className}__language-selector-label`}>
            <Globe size={24} />
            <span data-testid="span-selected-language">
              {currentLanguage?.toUpperCase()}
            </span>
          </span>
        ),
        isVisible: supportedLanguages.length >= 2,
      },
      {
        id: 'currency',
        title: t('selector.currency', 'Currency'),
        component: (
          <CurrencySwitch
            rootClassName={globalPreferenceClassName}
            onPanelClose={() => togglePanelVisibility(false)}
          />
        ),
        showTo: ['simlocal'],
        buttonLabel: currency && (
          <span data-testid="span-selected-currency" data-value={currency}>
            {getCurrencyCode(currentLanguage, currency)}
          </span>
        ),
        isVisible: !!currency,
      },
      {
        id: 'store',
        title: t('selector.store', 'Store'),
        component: (
          <CountrySwitch
            className={globalPreferenceClassName}
            onPanelClose={() => togglePanelVisibility(false)}
          />
        ),
        showTo: ['epay', 'xesim'],
        buttonLabel: <Icon name="storeFront" />,
        isVisible: true,
      },
    ],
    [t, supportedLanguages, currentLanguage, currency],
  );

  const visibleSelectors: SelectorType[] = useMemo(
    () => selectors.filter((selector) => selector.isVisible),
    [selectors],
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsActiveScroll(window.scrollY !== 0);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      ref={ref}
      className={classNames(`${className}`, {
        [`${className}--scroll`]: isActiveScroll,
      })}
    >
      <ShowTo brand="simlocal">
        <div className={`${className}__contact-bar`}>
          <div className={`${className}__contact-bar-container`}>
            <div
              tabIndex={0}
              className={`${className}__contact-bar hide-mobile`}
              onClick={handleStoreLocatorClicked}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleStoreLocatorClicked();
                }
              }}
            >
              <Icon name="storeFront" color="white" />
              <div>{t('navbar.storeLocator', 'Store Locator')}</div>
            </div>

            {WHATSAPP_CONTACT_NUMBER && (
              <div>
                <a
                  className={`${className}__contact-bar ${className}__contact-bar-whatsapp`}
                  target="_BLANK"
                  rel="noreferrer"
                  href={`https://wa.me/${WHATSAPP_CONTACT_NUMBER}`}
                >
                  <div
                    className={`${className}__contact-bar-whatsapp__message-us flex gap-4`}
                  >
                    <WhatsappLogo size={20} />
                    <span>
                      {t(
                        'navbar.message_us_on_whatsapp',
                        'Message Us On WhatsApp',
                      )}
                    </span>
                  </div>
                </a>
              </div>
            )}

            {contactBarItems.map((item) => {
              if (!item.visible) return null;
              return (
                <div
                  key={item.id}
                  className={`${className}__contact-bar ${className}__contact-bar-help hide-mobile`}
                >
                  <MenuItem
                    key={item.id}
                    item={item}
                    variant="contact-bar"
                    handleNavItemClick={handleNavItemClick}
                    handleSubMenuClose={handleSubMenuClose}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </ShowTo>

      {isBurgerMenuExpanded && expandedSubMenuId ? (
        <div
          className={`${className}__container ${className}__container--back`}
        >
          <Button
            variant="text"
            icon={<CaretLeft size={24} />}
            onClick={() => setExpandedSubMenuId('')}
          >
            {t('navbar.back', 'Back')}
          </Button>
          {renderBurgerMenuButton}
        </div>
      ) : (
        <div className={`${className}__container`}>
          {shouldRedirectToExternalHomepage ? (
            <a href={getHomepageUrl()} className={`${className}__brand`}>
              {brandImage}
            </a>
          ) : (
            <Link to="/" className={`${className}__brand`}>
              {brandImage}
            </Link>
          )}
          <div className={`${className}__menu`}>{renderMenu}</div>
          <div className={`${className}__language_switch_currency_selector`}>
            <GlobalPreferencePanelDropDown
              selectors={visibleSelectors}
              anchor={
                <GlobalPreferencePanelButton
                  selectors={visibleSelectors}
                  variant="dropdown"
                  isFocused={isGlobalPreferencePanelExpanded}
                />
              }
              isExpanded={isGlobalPreferencePanelExpanded}
              onPanelToggle={togglePanelVisibility}
            />
            <GlobalPreferencePanelButton
              selectors={visibleSelectors}
              variant="full-screen"
              isFocused={isFullScreenGlobalPreferencePanelOpened}
              onClick={() => {
                setIsFullScreenGlobalPreferencePanelOpened(
                  !isFullScreenGlobalPreferencePanelOpened,
                );
                if (!isFullScreenGlobalPreferencePanelOpened)
                  setIsBurgerMenuExpanded(false);
              }}
            />
          </div>
          <FreeTrialBanner variant="navbar" />

          <div className={`${className}__site-preferences`}>
            {renderBurgerMenuButton}
          </div>
        </div>
      )}

      <FullScreenGlobalPreferencePanel
        isExpanded={isFullScreenGlobalPreferencePanelOpened}
        onClose={() => {
          setIsFullScreenGlobalPreferencePanelOpened(false);
        }}
        selectors={visibleSelectors}
      />

      <BurgerMenu
        isExpanded={isBurgerMenuExpanded}
        expandedSubMenuId={expandedSubMenuId}
        handleNavItemClick={handleNavItemClick}
      />
    </header>
  );
};

export default forwardRef<HTMLElement, Props>(Navbar);
